<script lang="ts" setup>
import { Mailing } from '@/@types'
import type { Order, OrdersnapShot } from '@/@types/order'

import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useForm } from '@inertiajs/vue3'
import { Edit, Loader2 } from 'lucide-vue-next'

interface OrderShow extends Order {
    order_snap_shot: OrdersnapShot
}

const props = defineProps<{
    order: OrderShow
    mailing: Mailing
}>()

const form = useForm({
    group: props.mailing.group,
})
</script>

<template>
    <div class="flex flex-col gap-2">
        <Dialog v-if="mailing.status !== 'canceled'">
            <DialogTrigger class="flex cursor-pointer items-center gap-4" aria-label="Mudar grupo do pedido">
                {{ $t('Group') }}

                <Edit stroke-width="1" />
            </DialogTrigger>
            <DialogContent class="w-full max-w-xl">
                <DialogHeader>
                    <DialogTitle>Selecione um grupo</DialogTitle>
                    <DialogDescription class="text-gray-800">Os ingressos serão atribuídos ao grupo em que você selecionar.</DialogDescription>
                </DialogHeader>
                <form
                    @submit.prevent="
                        form.patch(`/admin/mailings/${mailing.id}/update_group`, {
                            preserveState: false,
                        })
                    "
                >
                    <div class="flex flex-col gap-1.5">
                        <Label required for="cancel_justification">{{ $t('Group') }}</Label>
                        <Select v-model="form.group">
                            <SelectTrigger class="w-full">
                                <SelectValue :placeholder="$t('Select')" />
                            </SelectTrigger>
                            <SelectContent class="z-[200]">
                                <SelectItem
                                    v-for="group in [
                                        'purchased_ticket',
                                        'speaker',
                                        'sponsor',
                                        'summit_access',
                                        'press',
                                        'production',
                                        'exhibitor',
                                        'courtesy',
                                        'staff',
                                        'supporter',
                                        'guest',
                                    ]"
                                    :key="`${group}-groups-${mailing.id}`"
                                    :value="group"
                                >
                                    {{ $t(`groups.${group}`) }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        <p v-if="form.errors.group" class="text-destructive text-xs">
                            {{ form.errors.group }}
                        </p>
                    </div>
                    <div class="mt-6 flex justify-between gap-2">
                        <DialogClose class="flex-1">
                            <Button type="button" variant="ghost" class="text-primary hover:text-primary hover:bg-primary/10 w-full">
                                {{ $t('Back') }}
                            </Button>
                        </DialogClose>
                        <Button :disabled="form.processing" class="flex-1">
                            <template v-if="!form.processing"> Atribuir grupo </template>
                            <template v-if="form.processing">
                                <span>
                                    <Loader2 class="animate-spin" />
                                    <span class="sr-only"></span>
                                </span>
                            </template>
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
        <div v-else>
            {{ $t('Group') }}
        </div>
        <p class="text-base font-semibold">
            {{ $t(`groups.${mailing.group}`) }}
        </p>
    </div>
</template>
